import { readVehicle } from '@theme/tt-vehicle-finder';

/**
 * Prefills the quote form from what the shopper already told us: the
 * vehicle saved by the finder, and the ?product= handle the product page
 * appends to its quote link.
 *
 * Only fills empty fields — a shopper who has started typing outranks
 * anything stored earlier.
 *
 * The product name is fetched rather than derived from the handle. Ten or so
 * handles in this catalogue were left over from duplicated products and name
 * a different truck than the title does: un-slugging the Haval H9's handle
 * produces "Gwm Poer Power Cannonrear Leaf 2019 Lift Kit", so a shopper
 * asking about one truck sent a request naming another, and the reply would
 * have quoted the wrong kit. The handle still travels, in a hidden field, so
 * whoever answers has an identifier that cannot be misread.
 */
class QuotePrefill extends HTMLElement {
  #cleanup;
  connectedCallback() {
    if (this.#cleanup) return;
    const form = this.closest('form');
    const submit = form?.querySelector('button[type="submit"]');
    const originalLabel = submit?.textContent;
    let pending = false;
    let resetTimer;
    const restore = () => {
      clearTimeout(resetTimer);
      pending = false;
      if (submit) {
        submit.disabled = false;
        submit.textContent = originalLabel;
      }
    };
    const onSubmit = (event) => {
      if (pending) { event.preventDefault(); return; }
      pending = true;
      if (submit) {
        submit.disabled = true;
        submit.textContent = submit.dataset.submitting || originalLabel;
      }
      // A challenge or interrupted navigation must not leave the form locked.
      resetTimer = setTimeout(restore, 15000);
    };
    form?.addEventListener('submit', onSubmit);
    window.addEventListener('pageshow', restore);
    this.#cleanup = () => {
      clearTimeout(resetTimer);
      form?.removeEventListener('submit', onSubmit);
      window.removeEventListener('pageshow', restore);
    };
    form?.querySelector('[data-quote-errors]')?.focus();
    const vehicle = readVehicle();
    if (vehicle) {
      const label = [vehicle.make, vehicle.model, vehicle.year || null].filter(Boolean).join(' ');
      this.#fill('vehicle', label);
    }

    const handle = new URLSearchParams(window.location.search).get('product');
    if (!handle) return;

    // Named so it reads as a line in the email Shopify sends.
    this.#addHidden('contact[Product code]', handle);

    // Fall back immediately so the field is never empty, then correct it if
    // the lookup succeeds. A slow or failed request leaves the shopper with
    // the old imperfect name rather than a blank field.
    this.#fill('product', this.#titleise(handle));
    this.#useRealTitle(handle);
  }

  disconnectedCallback() {
    this.#cleanup?.();
    this.#cleanup = null;
  }

  /** @param {string} handle */
  async #useRealTitle(handle) {
    try {
      // Guarded: the global is always present on a Shopify storefront, but a
      // missing one would throw inside connectedCallback and take the vehicle
      // prefill down with it.
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
      if (!response.ok) return;

      const product = await response.json();
      if (!product?.title) return;

      const field = this.querySelector('[data-prefill="product"]');
      // Overwrite only the placeholder we just wrote — never a shopper's typing.
      if (field && field.value === this.#titleise(handle)) field.value = product.title;
      const link = this.querySelector('[data-quote-product-link]');
      if (link) {
        link.textContent = product.title;
        link.href = `${root}products/${encodeURIComponent(handle)}`;
        this.querySelector('[data-quote-product-summary]').hidden = false;
      }
    } catch {
      // Keep the fallback.
    }
  }

  /**
   * @param {string} name
   * @param {string} value
   */
  #addHidden(name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    this.append(input);
  }

  /**
   * @param {string} name
   * @param {string} value
   */
  #fill(name, value) {
    const field = this.querySelector(`[data-prefill="${name}"]`);
    if (field && !field.value) field.value = value;
  }

  /**
   * @param {string} handle
   */
  #titleise(handle) {
    return handle
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

if (!customElements.get('tt-quote-prefill')) {
  customElements.define('tt-quote-prefill', QuotePrefill);
}
